import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { accountAuthError, completePassword, forgotPassword } from "../api/accountAuthApi";
import PasswordFields, { authButton, authInput, passwordError } from "../components/auth/PasswordFields";
import { authStore } from "../util/authStore";
import logoDocLift from "../images/logoDocLift.png";

export default function PasswordRecoveryPage({ purpose = "forgot" }) {
  const [proof] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return { challenge_id: params.get("challenge_id") || "", token: params.get("token") || "" };
  });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const title = purpose === "forgot" ? "Forgot password" : purpose === "setup" ? "Set your password" : "Reset password";
  useEffect(() => {
    if (purpose !== "forgot") {
      window.history.replaceState(window.history.state, "", window.location.pathname);
    }
  }, [purpose]);
  const invalidLink = purpose !== "forgot" && (!proof.challenge_id || !proof.token);
  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (purpose !== "forgot") {
      const problem = passwordError(password, confirmation);
      if (problem) { setError(problem); return; }
    }
    setBusy(true);
    try {
      if (purpose === "forgot") await forgotPassword(email.trim());
      else {
        await completePassword(purpose, { ...proof, new_password: password });
        authStore.logout();
        setPassword(""); setConfirmation("");
      }
      setDone(true);
    } catch (err) { setError(accountAuthError(err)); }
    finally { setBusy(false); }
  };
  return <div className="flex min-h-screen flex-col bg-white font-sans text-[#0F2920]">
    <header className="sticky top-0 z-10 flex h-16 w-full items-center justify-between border-b border-[#E2EAEB] bg-white/95 px-5 backdrop-blur sm:px-10">
      <Link to="/" aria-label="DocLift home" className="inline-flex items-center">
        <img src={logoDocLift} alt="DocLift" className="h-auto w-32 sm:w-36" />
      </Link>
      <Link to="/login" className="rounded-lg bg-gradient-to-r from-[#2563EB] to-[#3B82F6] px-4 py-2 text-sm font-semibold text-white shadow-[0_2px_10px_rgba(37,99,235,0.3)] transition hover:from-[#3B82F6] hover:to-[#1D4ED8]">
        Sign in
      </Link>
    </header>
    <main className="flex flex-1 items-center justify-center bg-[linear-gradient(180deg,#F0F7FF_0%,#FFFFFF_100%)] px-4 py-12">
      <section className="w-full max-w-lg rounded-2xl border border-[#E2EAEB] border-t-4 border-t-[#2563EB] bg-white p-6 shadow-[0_12px_40px_rgba(37,99,235,0.10)] sm:p-9">
        <div className="mb-7 border-b border-[#F0F4F4] pb-5">
          <p className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-[#2563EB]">DocLift account</p>
          <h1 className="text-2xl font-bold tracking-tight text-[#0F2920]">{title}</h1>
          <p className="mt-2 text-sm leading-6 text-[#4A6A65]">
            {purpose === "setup"
              ? "Create a secure password to activate your account."
              : purpose === "reset"
                ? "Choose a new password for your DocLift account."
                : "Enter your email and we’ll send a secure password link if your account is eligible."}
          </p>
        </div>
        {error && <p role="alert" className="mb-4 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{error}</p>}
        {invalidLink ? <p role="alert" className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm leading-6 text-amber-900">This link is missing required information. Request a new link or ask your administrator to resend your invitation.</p>
          : done ? <p role="status" className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm leading-6 text-emerald-900">{purpose === "forgot"
        ? "If an eligible account exists, a password reset email will be sent. Check your inbox and spam folder."
        : "Your password has been saved. Sign in with your new password."}</p>
        : <form onSubmit={submit} className="space-y-4">
          {purpose === "forgot" ? <label className="block text-sm font-semibold">Email
            <input type="email" autoComplete="email" className={authInput} required value={email} onChange={(event) => setEmail(event.target.value)} />
          </label> : <PasswordFields password={password} confirmation={confirmation} onPassword={setPassword} onConfirmation={setConfirmation} />}
          <button className={`${authButton} w-full rounded-lg bg-gradient-to-r from-[#2563EB] to-[#3B82F6] px-4 py-3 shadow-[0_4px_12px_rgba(37,99,235,0.25)] transition hover:from-[#3B82F6] hover:to-[#1D4ED8] disabled:cursor-not-allowed disabled:opacity-50`} disabled={busy}>{busy ? "Please wait..." : purpose === "forgot" ? "Send reset link" : "Save password"}</button>
        </form>}
        <div className="mt-6 flex flex-col gap-3 border-t border-[#F0F4F4] pt-5 text-sm sm:flex-row sm:items-center sm:justify-between">
          {purpose !== "forgot" && <Link className="font-semibold text-[#2563EB] hover:text-[#1D4ED8]" to="/forgot-password">Request a new link</Link>}
          <Link className="font-semibold text-[#2563EB] hover:text-[#1D4ED8]" to="/login">Back to sign in</Link>
        </div>
      </section>
    </main>
    <footer className="bg-[#0b2034] px-6 py-6 text-center">
      <span className="mr-2 inline-block text-base font-extrabold text-white">DocLift</span>
      <span className="text-xs text-white/70">WhatsApp AI for small businesses · Built in India</span>
    </footer>
  </div>;
}
